import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Copy, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return null;
  const ms = new Date(target).getTime() - now;
  if (ms <= 0) return null;
  const d = Math.floor(ms / 86400000), h = Math.floor(ms / 3600000) % 24, m = Math.floor(ms / 60000) % 60, s = Math.floor(ms / 1000) % 60;
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
}

function CouponChip({ code, color }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.preventDefault();
    navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <button type="button" onClick={copy} title="Copy code" className="inline-flex items-center gap-1 font-mono font-bold px-2 py-0.5 rounded border border-dashed"
      style={{ borderColor: color, color }}>
      {code} {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

function CTA({ promo, className, style }) {
  if (!promo.cta_text || !promo.cta_link) return null;
  const external = /^https?:\/\//.test(promo.cta_link);
  return external
    ? <a href={promo.cta_link} className={className} style={style}>{promo.cta_text}</a>
    : <Link to={promo.cta_link} className={className} style={style}>{promo.cta_text}</Link>;
}

/** One promotion. variant: "bar" (slim strip) or "card" (larger banner). */
export function PromoBanner({ promo, variant = 'card', onDismiss, stacked = false }) {
  const countdown = useCountdown(promo.countdown_to);
  const style = { backgroundColor: promo.bg_color || '#FFD700', color: promo.text_color || '#000' };

  if (variant === 'bar') {
    return (
      <div style={style} className="relative text-sm px-10 py-2 text-center flex flex-wrap items-center justify-center gap-x-3 gap-y-1" data-testid="promo-bar">
        <span className="font-bold">{promo.headline}</span>
        {promo.message && <span className="opacity-80">{promo.message}</span>}
        {promo.coupon_code && <CouponChip code={promo.coupon_code} color={promo.text_color || '#000'} />}
        {countdown && <span className="font-mono text-xs opacity-80">Ends in {countdown}</span>}
        <CTA promo={promo} className="underline font-bold" />
        {onDismiss && <button onClick={onDismiss} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>}
      </div>
    );
  }
  return (
    <div style={style} className={`rounded-2xl overflow-hidden flex flex-col items-stretch ${stacked ? '' : 'sm:flex-row'}`} data-testid="promo-card">
      {promo.image && <img src={promo.image} alt="" className={stacked ? 'h-32 w-full object-cover' : 'sm:w-48 h-40 sm:h-auto object-cover'} />}
      <div className="p-5 flex-1 flex flex-col justify-center gap-2">
        <p className="text-xl font-black leading-tight">{promo.headline}</p>
        {promo.message && <p className="opacity-80 text-sm">{promo.message}</p>}
        <div className="flex flex-wrap items-center gap-3 mt-1">
          {promo.coupon_code && <span className="text-sm">Use code <CouponChip code={promo.coupon_code} color={promo.text_color || '#000'} /></span>}
          {countdown && <span className="font-mono text-sm font-bold">Ends in {countdown}</span>}
          {/* Button uses the promo's colors swapped, so it stands out on any background */}
          <CTA promo={promo} className="ml-auto px-5 py-2.5 rounded-full font-bold text-sm hover:opacity-90 transition-opacity"
            style={{ backgroundColor: promo.text_color || '#000', color: promo.bg_color || '#FFD700' }} />
        </div>
      </div>
    </div>
  );
}

const dismissedKey = 'dismissed_promos';
const readDismissed = () => { try { return JSON.parse(sessionStorage.getItem(dismissedKey) || '[]'); } catch { return []; } };

/** Fetches and shows the live promotions for one spot on the site. */
export default function PromoSlot({ placement, productId, className = '' }) {
  const [promos, setPromos] = useState([]);
  const [index, setIndex] = useState(0);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    axios.get(`${API}/promotions`, { params: { placement, product_id: productId || '' } })
      .then(r => setPromos(r.data.promotions)).catch(() => setPromos([]));
  }, [placement, productId]);

  const visible = promos.filter(p => !(placement === 'top_bar' && dismissed.includes(p.id)));

  // The top bar rotates through several promotions
  useEffect(() => {
    if (placement !== 'top_bar' || visible.length < 2) return undefined;
    const t = setInterval(() => setIndex(i => (i + 1) % visible.length), 6000);
    return () => clearInterval(t);
  }, [placement, visible.length]);

  if (!visible.length) return null;
  if (placement === 'top_bar') {
    const p = visible[index % visible.length];
    const dismiss = () => {
      const next = [...dismissed, p.id];
      setDismissed(next);
      try { sessionStorage.setItem(dismissedKey, JSON.stringify(next)); } catch {}
    };
    return <PromoBanner promo={p} variant="bar" onDismiss={dismiss} />;
  }
  return (
    <div className={`space-y-3 ${className}`}>
      {/* Product pages and the cart put promos in narrow columns, so stack the image on top there */}
      {visible.map(p => <PromoBanner key={p.id} promo={p} stacked={placement === 'cart' || placement === 'product'} />)}
    </div>
  );
}
